import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { CreateHubForm } from "@/components/HubForms";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const ROLE = { owner: "Owner", coparent: "Co-parent", relative: "Relative", kid: "Kid", admin: "Admin · demo", support: "Support view" };

export default function HubSwitcher({ hub, onNavigate }) {
  const { session, refresh, signIn } = useAuth();
  const nav = useNavigate();
  const [creating, setCreating] = useState(false);
  const hubs = session?.hubs || [];

  const trigger = (
    <div className="flex w-full items-center gap-2 rounded-lg border border-q-line bg-q-surface px-2.5 py-2 text-left transition-[background-color,border-color] duration-150 hover:border-q-faint">
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] font-bold text-q-text" data-testid="hub-switcher-current">{hub.name}</div>
        <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-q-faint">{ROLE[hub.my_role]}</div>
      </div>
      {(session?.type !== "kid" || hubs.length > 1) && <ChevronsUpDown className="h-3.5 w-3.5 flex-shrink-0 text-q-faint" />}
    </div>
  );

  const isKid = session?.type === "kid";
  if (isKid && hubs.length < 2) return trigger;

  const pick = async (id) => {
    if (isKid) {
      if (id === hub.id) return;
      try {
        signIn((await api.post("/auth/kid/switch", { organization_id: id })).data);
      } catch (e) { toast.error(errMsg(e)); return; }
    }
    nav(`/h/${id}/home`);
    onNavigate?.();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="w-full" data-testid="hub-switcher-btn">{trigger}</button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="q-chrome w-[230px] border-q-line bg-q-surface" data-testid="hub-switcher-menu">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.16em] text-q-faint">Your hubs</DropdownMenuLabel>
          {hubs.map((h) => (
            <DropdownMenuItem key={h.id} onSelect={() => pick(h.id)} className="cursor-pointer gap-2" data-testid={`hub-switcher-item-${h.id}`}>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold">{h.name}</span>
                <span className="block text-[10px] uppercase tracking-[0.12em] text-q-faint">{ROLE[h.role]}</span>
              </span>
              {h.id === hub.id && <Check className="h-3.5 w-3.5 text-q-accent" />}
            </DropdownMenuItem>
          ))}
          {!isKid && <><DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setCreating(true)} className="cursor-pointer gap-2 text-q-accent" data-testid="hub-switcher-create">
            <Plus className="h-3.5 w-3.5" /> Create another hub
          </DropdownMenuItem></>}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="border-q-line bg-q-surface sm:max-w-md" data-testid="create-hub-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold">Create another hub</DialogTitle>
            <DialogDescription className="text-q-muted">You'll be the Owner of the new hub.</DialogDescription>
          </DialogHeader>
          <CreateHubForm
            onCreated={async (h) => {
              setCreating(false);
              await refresh();
              pick(h.id);
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
